import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { toast } from 'react-toastify'
import { DrawioNode } from './DrawioNode'
import { normalizeEditorHtml, transformPastedHtml } from './normalizeEditorHtml'
import type { DrawioBlockValue } from '../homework/drawio/drawioHtml'
import './TipTapRichTextEditor.css'

export type TipTapRichTextEditorProps = {
  value: string
  onChange: (html: string) => void
  className?: string
  placeholder?: string
  onUploadingChange?: (uploading: boolean) => void
  uploadImage?: (file: File) => Promise<string>
  /** Enable draw.io node + toolbar button */
  enableDrawio?: boolean
  onDrawioRequest?: (mode: 'new' | 'edit', value?: DrawioBlockValue) => void
  minHeightClass?: string
}

export type TipTapRichTextEditorHandle = {
  getHtml: () => string
  getEditor: () => Editor | null
  insertDrawio: (value: DrawioBlockValue) => void
  updateDrawio: (value: DrawioBlockValue) => void
}

function isEmptyHtml(html: string): boolean {
  const t = (html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
  return !t
}

const TipTapRichTextEditor = forwardRef<TipTapRichTextEditorHandle, TipTapRichTextEditorProps>(
  function TipTapRichTextEditor(
    {
      value,
      onChange,
      className = '',
      placeholder = 'Nhập nội dung…',
      onUploadingChange,
      uploadImage,
      enableDrawio = false,
      onDrawioRequest,
      minHeightClass,
    },
    ref
  ) {
    const reactId = useId().replace(/:/g, '')
    const emittedRef = useRef(normalizeEditorHtml(value || ''))
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const uploadRef = useRef(uploadImage)
    uploadRef.current = uploadImage
    const onUploadingRef = useRef(onUploadingChange)
    onUploadingRef.current = onUploadingChange
    const onDrawioRef = useRef(onDrawioRequest)
    onDrawioRef.current = onDrawioRequest
    const wrapRef = useRef<HTMLDivElement | null>(null)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [hoverSize, setHoverSize] = useState({ r: 3, c: 3 })
    const [tableActive, setTableActive] = useState(false)

    const extensions = useMemo(() => {
      const list: any[] = [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          codeBlock: {},
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        Image.configure({ inline: false, allowBase64: false }),
        Placeholder.configure({ placeholder }),
        Table.configure({
          resizable: true,
          HTMLAttributes: { class: 'rte-table' },
        }),
        TableRow,
        TableHeader,
        TableCell,
      ]
      if (enableDrawio) list.push(DrawioNode)
      return list
    }, [placeholder, enableDrawio])

    const editor = useEditor({
      immediatelyRender: false,
      extensions,
      content: normalizeEditorHtml(value || '') || '<p></p>',
      editorProps: {
        attributes: {
          class: `tiptap-editor-body${minHeightClass ? ` ${minHeightClass}` : ''}`,
        },
        transformPastedHTML: (html) => transformPastedHtml(html),
        handleClick: (_view, _pos, event) => {
          if (!enableDrawio) return false
          const t = event.target as HTMLElement
          if (!t?.closest?.('.hw-drawio-preview') && !t.classList?.contains('hw-drawio-preview')) {
            return false
          }
          const block = t.closest('.hw-drawio') as HTMLElement | null
          if (!block) return false
          const diagramId = block.getAttribute('data-diagram-id') || ''
          const pngUrl = block.getAttribute('data-png-url') || ''
          const xmlUrl = block.getAttribute('data-xml-url') || ''
          const title =
            block.querySelector('.hw-drawio-title')?.textContent?.trim() || 'Sơ đồ quy trình'
          const version = Number(block.getAttribute('data-version') || '1') || 1
          if (!diagramId || !pngUrl) return false
          event.preventDefault()
          onDrawioRef.current?.('edit', { diagramId, pngUrl, xmlUrl, title, version })
          return true
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML()
        emittedRef.current = html
        onChangeRef.current(html)
      },
      onSelectionUpdate: ({ editor: ed }) => {
        setTableActive(ed.isActive('table'))
      },
    })

    // Sync external value (load form) when not focused
    useEffect(() => {
      if (!editor) return
      const next = normalizeEditorHtml(value || '')
      if (next === emittedRef.current) return
      if (editor.isFocused) {
        emittedRef.current = editor.getHTML()
        return
      }
      const content = next && !isEmptyHtml(next) ? next : '<p></p>'
      editor.commands.setContent(content, { emitUpdate: false })
      emittedRef.current = editor.getHTML()
    }, [value, editor])

    useImperativeHandle(
      ref,
      () => ({
        getHtml: () => {
          const html = editor?.getHTML() || emittedRef.current || ''
          emittedRef.current = html
          return html
        },
        getEditor: () => editor,
        insertDrawio: (v) => {
          editor?.chain().focus().insertDrawio(v).run()
          emittedRef.current = editor?.getHTML() || ''
          onChangeRef.current(emittedRef.current)
        },
        updateDrawio: (v) => {
          editor?.chain().focus().updateDrawio(v).run()
          emittedRef.current = editor?.getHTML() || ''
          onChangeRef.current(emittedRef.current)
        },
      }),
      [editor]
    )

    useEffect(() => {
      if (!pickerOpen) return
      const onDoc = (e: MouseEvent) => {
        if (!wrapRef.current?.contains(e.target as Node)) setPickerOpen(false)
      }
      document.addEventListener('mousedown', onDoc)
      return () => document.removeEventListener('mousedown', onDoc)
    }, [pickerOpen])

    const runImageUpload = useCallback(async () => {
      if (!uploadRef.current) {
        toast.info('Chưa cấu hình upload ảnh')
        return
      }
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = async () => {
        const file = input.files?.[0]
        if (!file || !editor) return
        try {
          onUploadingRef.current?.(true)
          const url = await uploadRef.current!(file)
          editor.chain().focus().setImage({ src: url }).run()
        } catch (err: any) {
          toast.error(err?.message || 'Upload ảnh thất bại')
        } finally {
          onUploadingRef.current?.(false)
        }
      }
      input.click()
    }, [editor])

    const insertTable = useCallback(
      (rows: number, cols: number) => {
        editor?.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run()
        setPickerOpen(false)
      },
      [editor]
    )

    const setLink = useCallback(() => {
      if (!editor) return
      const prev = editor.getAttributes('link').href as string | undefined
      const url = window.prompt('URL liên kết', prev || 'https://')
      if (url === null) return
      if (!url.trim()) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
    }, [editor])

    if (!editor) {
      return (
        <div className={`tiptap-rte ${className}`}>
          <div className="tiptap-rte-loading text-sm text-slate-500 p-3">Đang tải editor…</div>
        </div>
      )
    }

    return (
      <div className={`tiptap-rte admin-rte ${className}`} ref={wrapRef} data-rte-id={reactId}>
        <div className="tiptap-toolbar admin-rte-toolbar">
          <div className="tiptap-toolbar-group">
            <select
              className="tiptap-select"
              value={
                editor.isActive('heading', { level: 1 })
                  ? '1'
                  : editor.isActive('heading', { level: 2 })
                    ? '2'
                    : editor.isActive('heading', { level: 3 })
                      ? '3'
                      : ''
              }
              onChange={(e) => {
                const v = e.target.value
                if (!v) editor.chain().focus().setParagraph().run()
                else
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({ level: Number(v) as 1 | 2 | 3 })
                    .run()
              }}
              title="Heading"
            >
              <option value="">Paragraph</option>
              <option value="1">H1</option>
              <option value="2">H2</option>
              <option value="3">H3</option>
            </select>
          </div>
          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={editor.isActive('bold') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            >
              B
            </button>
            <button
              type="button"
              className={editor.isActive('italic') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              I
            </button>
            <button
              type="button"
              className={editor.isActive('underline') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              title="Underline"
            >
              U
            </button>
            <button
              type="button"
              className={editor.isActive('strike') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              title="Strike"
            >
              S
            </button>
            <button
              type="button"
              className={editor.isActive('blockquote') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              title="Quote"
            >
              “
            </button>
          </div>
          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={editor.isActive('bulletList') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Bullet list"
            >
              •
            </button>
            <button
              type="button"
              className={editor.isActive('orderedList') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Ordered list"
            >
              1.
            </button>
          </div>
          <div className="tiptap-toolbar-group">
            <button type="button" onClick={setLink} title="Link">
              Link
            </button>
            <button type="button" onClick={() => void runImageUpload()} title="Image">
              Ảnh
            </button>
            <button
              type="button"
              className={editor.isActive('codeBlock') ? 'is-active' : ''}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              title="Code / Mermaid"
            >
              {'</>'}
            </button>
            {enableDrawio && (
              <button
                type="button"
                onClick={() => onDrawioRef.current?.('new')}
                title="Draw.io"
              >
                Draw.io
              </button>
            )}
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              title="Chèn bảng"
            >
              Bảng
            </button>
          </div>
          <div className="tiptap-toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              title="Clear format"
            >
              Clear
            </button>
          </div>
          {tableActive && (
            <div className="tiptap-toolbar-group rte-table-actions-inline">
              <button
                type="button"
                className="rte-icon-btn"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Thêm cột"
              >
                +C
              </button>
              <button
                type="button"
                className="rte-icon-btn"
                onClick={() => editor.chain().focus().deleteColumn().run()}
                title="Xóa cột"
              >
                −C
              </button>
              <button
                type="button"
                className="rte-icon-btn"
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Thêm hàng"
              >
                +H
              </button>
              <button
                type="button"
                className="rte-icon-btn"
                onClick={() => editor.chain().focus().deleteRow().run()}
                title="Xóa hàng"
              >
                −H
              </button>
              <button
                type="button"
                className="rte-icon-btn"
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Xóa bảng"
              >
                ×T
              </button>
            </div>
          )}
        </div>

        {pickerOpen && (
          <div className="rte-table-picker" role="dialog" aria-label="Chọn kích thước bảng">
            <div className="rte-table-picker-grid">
              {Array.from({ length: 6 }).map((_, r) =>
                Array.from({ length: 6 }).map((_, c) => {
                  const active = r < hoverSize.r && c < hoverSize.c
                  return (
                    <button
                      key={`${r}-${c}`}
                      type="button"
                      className={`rte-table-picker-cell${active ? ' is-active' : ''}`}
                      onMouseEnter={() => setHoverSize({ r: r + 1, c: c + 1 })}
                      onClick={() => insertTable(r + 1, c + 1)}
                    />
                  )
                })
              )}
            </div>
            <div className="rte-table-picker-label">
              {hoverSize.r} × {hoverSize.c}
            </div>
          </div>
        )}

        <EditorContent editor={editor} className="tiptap-content admin-rte-quill" />
      </div>
    )
  }
)

export default TipTapRichTextEditor
